/**
 * Settings Store Registry
 *
 * Bridges platform-instantiated stores to shared components.
 * Platforms register stores in App.tsx; shared components access via hooks.
 *
 * This registry solves exactly one problem: shared components can't import
 * from @edgerunner/electron (breaks Lerna dependency direction), so they
 * need a way to access platform-instantiated stores.
 */

import { StoreApi, UseBoundStore } from 'zustand';
import { UserSettingsStore } from './userSettingsStore';
import { BackendOptionsStore } from './backendOptionsStore';

type UserSettingsStoreHook = UseBoundStore<StoreApi<UserSettingsStore>>;
type BackendOptionsStoreHook = UseBoundStore<StoreApi<BackendOptionsStore>>;

let _useUserSettings: UserSettingsStoreHook | null = null;
let _useBackendOptions: BackendOptionsStoreHook | null = null;

/**
 * Register platform-specific settings stores.
 * Call once from platform App.tsx before rendering.
 */
export function registerSettingsStores(
	userSettings: UserSettingsStoreHook,
	backendOptions?: BackendOptionsStoreHook
): void {
	_useUserSettings = userSettings;
	_useBackendOptions = backendOptions ?? null;
}

/**
 * Access user settings store from shared components.
 * @throws Error if stores not registered
 */
export function useUserSettings(): UserSettingsStore {
	if (!_useUserSettings) {
		throw new Error(
			'Settings stores not registered. Call registerSettingsStores() in your platform App.tsx first.'
		);
	}
	return _useUserSettings();
}
export function getUserSettingsStoreHook(): UserSettingsStoreHook | null {
	return _useUserSettings;
}


export function getUserSettingsState(): UserSettingsStore | null {
	const store = _useUserSettings as unknown as {
		getState: () => UserSettingsStore;
	} | null;
	return store?.getState() ?? null;
}

/**
 * Access backend options store from shared components.
 * Returns undefined if platform doesn't support backend options (e.g., web).
 */
export function useBackendOptions(): BackendOptionsStore | undefined {
	return _useBackendOptions?.();
}

/**
 * Trigger load on all registered settings stores.
 * Called after backend connection is confirmed ready.
 */
export function loadRegisteredStores(): void {
	if (_useUserSettings) {
		_useUserSettings.getState().actions.load().catch((err: unknown) =>
			console.error('Failed to load user settings:', err)
		);
	}
	if (_useBackendOptions) {
		_useBackendOptions.getState().actions.load().catch((err: unknown) =>
			console.error('Failed to load backend options:', err)
		);
	}
}
